import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '../../interfaces/typography';
import { Save, X, Mic, Loader2, Plus, StopCircle, Play, Trash2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addInboundRoute, updateInboundRoute } from '../../lib/redux/routes/slice';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { InboundRoute } from '../../interfaces/bot-config';
import { ConversationType, RecordedGreeting, AddressType, AgentType } from '../../interfaces/db';
import { PhoneNumberSelector } from '../Configuration/Routes/components/PhoneNumberSelector';
import { SimplifiedSingleSelect } from '../Selectors/SimplifiedSingleSelect';
import { AudioRecorder } from '../../lib/recorder';
import { getServicesManager } from '../../services';

interface RouteInlineEditorProps {
  route: InboundRoute | null;
  onSave: () => void;
  onCancel: () => void;
}

type RoutingTarget = 'team' | 'user';

export const RouteInlineEditor: React.FC<RouteInlineEditorProps> = ({ route, onSave, onCancel }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { allUsers, team, sortedUserGroupInfo } = useUserData();
  const [recordedGreetings, setRecordedGreetings] = useState<RecordedGreeting[]>([]);
  const [isLoadingGreetings, setIsLoadingGreetings] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(route?.address || '');
  const [routingTarget, setRoutingTarget] = useState<RoutingTarget>('team');
  const [targetId, setTargetId] = useState('');

  // Voicemail greeting recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedGreetingId, setRecordedGreetingId] = useState<string | null>(null);
  const [isUploadingGreeting, setIsUploadingGreeting] = useState(false);
  const mediaRecorderRef = useRef<AudioRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch recorded greetings on mount
  useEffect(() => {
    getServicesManager().getGreetings().then((fetchedGreetings: RecordedGreeting[] | null) => {
      if (fetchedGreetings) {
        setRecordedGreetings(fetchedGreetings);
      }
      setIsLoadingGreetings(false);
    }).catch((error) => {
      console.error('Failed to fetch greetings:', error);
      setIsLoadingGreetings(false);
    });
  }, []);

  // Initialize form when editing
  useEffect(() => {
    if (route) {
      setPhoneNumber(route.address);
      if (route.agent_type === 'USER') {
        setRoutingTarget('user');
        setTargetId(route.agent_id);
      } else {
        setRoutingTarget('team');
        // Parse notify_id and greeting_id from agent_id format: __IVR_NOTIFY_VM_{notify_id}_{greeting_id}__
        const match = route.agent_id.match(/__IVR_NOTIFY_VM_([^_]+)_([^_]*)__/);
        if (match) {
          const notify_id = match[1];
          const greeting_id = match[2];
          setTargetId(notify_id);
          if (greeting_id) {
            setRecordedGreetingId(greeting_id);
          }
        } else {
          setTargetId(team?.team_id || '');
        }
      }
    }
  }, [route, team]);

  // Recording timer effect
  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      setRecordingTime(0);
    }

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isRecording]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartRecording = async () => {
    try {
      const recorder = new AudioRecorder();
      await recorder.ready;
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setAudioBlob(null);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to start recording. Please check microphone permissions.');
    }
  };

  const handleStopRecording = async () => {
    if (!mediaRecorderRef.current) return;

    try {
      await mediaRecorderRef.current.ensureStopped();
      const blob = await mediaRecorderRef.current.blob;
      setAudioBlob(blob);
      setIsRecording(false);
    } catch (error) {
      console.error('Failed to stop recording:', error);
      alert('Failed to stop recording. Please try again.');
    }
  };

  const handleSaveRecording = async () => {
    if (!audioBlob) return;

    setIsUploadingGreeting(true);
    try {
      // Convert blob to file
      const timestamp = new Date().getTime();
      const file = new File([audioBlob], `greeting-${timestamp}.webm`, { type: audioBlob.type });
      const greetingName = `Greeting ${new Date().toLocaleString()}`;

      const result = await getServicesManager().uploadGreeting(greetingName, file);

      if (result) {
        setRecordedGreetingId(result.recorded_greeting_id);
        setAudioBlob(null);

        // Refresh greetings list
        const fetchedGreetings = await getServicesManager().getGreetings();
        if (fetchedGreetings) {
          setRecordedGreetings(fetchedGreetings);
        }

        alert('Voicemail greeting saved successfully!');
      } else {
        throw new Error('Upload returned null');
      }
    } catch (error) {
      console.error('Failed to upload greeting:', error);
      alert('Failed to save voicemail greeting. Please try again.');
    } finally {
      setIsUploadingGreeting(false);
    }
  };

  const handleDiscardRecording = () => {
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const handleRemoveGreeting = () => {
    setRecordedGreetingId(null);
  };

  const handleSave = async () => {
    if (!phoneNumber.trim()) {
      alert('Please select a phone number');
      return;
    }

    if (routingTarget === 'user' && !targetId) {
      alert('Please select a user');
      return;
    }

    setIsSaving(true);
    try {
      let agent_type: AgentType;
      let agent_id: string;

      if (routingTarget === 'user') {
        agent_type = AgentType.USER;
        agent_id = targetId;
      } else {
        // Team/subteam route
        agent_type = AgentType.IVR;
        const notify_id = targetId || team?.team_id || '';
        const greeting_id = recordedGreetingId || '';
        agent_id = `__IVR_NOTIFY_VM_${notify_id}_${greeting_id}__`;
      }

      // Normalize phone number (remove + prefix if present)
      const normalizedPhone = phoneNumber.replace(/^\+/, '');

      const routeData: Partial<InboundRoute> = {
        address: normalizedPhone,
        address_type: AddressType.PHONE,
        agent_type,
        agent_id,
        team_id: team?.team_id,
        conversation_type: ConversationType.CALL,
      };

      if (route) {
        await dispatch(updateInboundRoute({ routeId: route.route_id, routeData })).unwrap();
      } else {
        await dispatch(addInboundRoute(routeData)).unwrap();
      }

      onSave();
    } catch (error) {
      console.error('Failed to save route:', error);
      alert('Failed to save route. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <tr className="bg-blue-50 border-l-4 border-l-blue-500">
      <td colSpan={5} className="px-6 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 flex items-center justify-between">
            <div>
              <Typography variant="h4" color="#1F2937" fontWeight={600}>
                {route ? 'Edit Route' : 'Add New Route'}
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280" style={{ marginTop: '0.25rem' }}>
                Configure phone number routing for your team
              </Typography>
            </div>
            <div className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full">
              <Typography variant="smallParagraph" color="#2563EB" fontWeight={500}>
                {route ? 'Editing' : 'Creating'}
              </Typography>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Phone Number Row */}
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.75rem' }}>
                Phone Number <span className="text-red-500">*</span>
              </Typography>
              <PhoneNumberSelector
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
            </div>

            {/* Route To Row */}
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.75rem' }}>
                Route To <span className="text-red-500">*</span>
              </Typography>

              {/* Toggle between Team and User */}
              <div className="flex gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setRoutingTarget('team');
                    setTargetId('');
                  }}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    routingTarget === 'team'
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-white border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Typography
                    variant="mediumParagraph"
                    color={routingTarget === 'team' ? '#2563EB' : '#6B7280'}
                    fontWeight={routingTarget === 'team' ? 600 : 400}
                  >
                    Team/Subteam
                  </Typography>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRoutingTarget('user');
                    setTargetId('');
                  }}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all ${
                    routingTarget === 'user'
                      ? 'bg-purple-50 border-purple-500'
                      : 'bg-white border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Typography
                    variant="mediumParagraph"
                    color={routingTarget === 'user' ? '#7C3AED' : '#6B7280'}
                    fontWeight={routingTarget === 'user' ? 600 : 400}
                  >
                    User
                  </Typography>
                </button>
              </div>

              {/* Target Selection */}
              {routingTarget === 'team' ? (
                <SimplifiedSingleSelect
                  options={[
                    { value: '', label: `Main Team (${team?.team_name || team?.team || 'Team'})` },
                    ...sortedUserGroupInfo.map((subteam) => ({
                      value: subteam.group.user_group_id,
                      label: `Subteam: ${subteam.group.user_group_name}`
                    }))
                  ]}
                  value={targetId}
                  onChange={(value) => setTargetId(value || '')}
                  placeholder="Select team or subteam..."
                />
              ) : (
                <SimplifiedSingleSelect
                  options={allUsers.map((user) => ({
                    value: user.user_id,
                    label: user.user_name
                  }))}
                  value={targetId}
                  onChange={(value) => setTargetId(value || '')}
                  placeholder="Select a user..."
                />
              )}
            </div>

            {/* Voicemail Greeting Row (only for team routes) */}
            {routingTarget === 'team' && (
              <div>
                <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.75rem' }}>
                  Voicemail Greeting (Optional)
                </Typography>

                {/* Show selected greeting if exists */}
                {recordedGreetingId && !isRecording && !audioBlob && (
                  <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                      <Mic size={16} className="mr-2 text-green-600" />
                      <Typography variant="smallParagraph" color="#059669" fontWeight={500}>
                        Custom greeting selected
                      </Typography>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveGreeting}
                      className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors flex items-center gap-1"
                    >
                      <Trash2 size={14} />
                      <Typography variant="smallParagraph" color="#DC2626" fontWeight={500}>
                        Remove
                      </Typography>
                    </button>
                  </div>
                )}

                {/* Show recording interface */}
                {isRecording && (
                  <div className="px-4 py-3 bg-red-50 border border-red-300 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        <Typography variant="mediumParagraph" color="#DC2626" fontWeight={600}>
                          Recording... {formatTime(recordingTime)}
                        </Typography>
                      </div>
                      <button
                        type="button"
                        onClick={handleStopRecording}
                        className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-2"
                      >
                        <StopCircle size={16} />
                        <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                          Stop
                        </Typography>
                      </button>
                    </div>
                  </div>
                )}

                {/* Show audio preview with save/discard */}
                {audioBlob && !isRecording && (
                  <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <Play size={16} className="text-blue-600" />
                      <Typography variant="smallParagraph" color="#2563EB" fontWeight={500}>
                        Recording complete ({formatTime(recordingTime)})
                      </Typography>
                    </div>
                    <audio src={URL.createObjectURL(audioBlob)} controls className="w-full" />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSaveRecording}
                        disabled={isUploadingGreeting}
                        className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isUploadingGreeting ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                              Uploading...
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Save size={16} />
                            <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                              Save Greeting
                            </Typography>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleDiscardRecording}
                        disabled={isUploadingGreeting}
                        className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <X size={16} />
                        <Typography variant="smallParagraph" color="#374151" fontWeight={500}>
                          Discard
                        </Typography>
                      </button>
                    </div>
                  </div>
                )}

                {/* Show options when no greeting is active */}
                {!recordedGreetingId && !isRecording && !audioBlob && (
                  <div className="space-y-3">
                    {/* Select from existing greetings */}
                    {recordedGreetings && recordedGreetings.length > 0 && (
                      <div>
                        <Typography variant="smallParagraph" color="#6B7280" style={{ marginBottom: '0.5rem' }}>
                          Select an existing greeting:
                        </Typography>
                        <SimplifiedSingleSelect
                          options={recordedGreetings.map((greeting) => ({
                            value: greeting.recorded_greeting_id,
                            label: greeting.name || `Greeting ${greeting.recorded_greeting_id.substring(0, 8)}...`
                          }))}
                          value={recordedGreetingId || ''}
                          onChange={(value) => setRecordedGreetingId(value || null)}
                          placeholder="Choose a saved greeting..."
                        />
                      </div>
                    )}

                    {/* Divider */}
                    {recordedGreetings && recordedGreetings.length > 0 && (
                      <div className="flex items-center gap-3">
                        <div className="flex-1 border-t border-gray-300" />
                        <Typography variant="smallParagraph" color="#9CA3AF">
                          or
                        </Typography>
                        <div className="flex-1 border-t border-gray-300" />
                      </div>
                    )}

                    {/* Record new greeting button */}
                    <button
                      type="button"
                      onClick={handleStartRecording}
                      className="w-full px-4 py-3 bg-white border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 rounded-lg transition-all flex items-center justify-center gap-2 text-gray-700 hover:text-blue-600"
                    >
                      <Mic size={16} />
                      <Typography variant="mediumParagraph" color="inherit" fontWeight={500}>
                        Record New Greeting
                      </Typography>
                    </button>

                    {/* Info text */}
                    <Typography variant="smallParagraph" color="#9CA3AF" style={{ textAlign: 'center' }}>
                      Leave blank to use default system greeting
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <X size={16} />
              <Typography variant="smallParagraph" color="#374151" fontWeight={500}>
                Cancel
              </Typography>
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !phoneNumber}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                    Saving...
                  </Typography>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                    {route ? 'Update' : 'Create'} Route
                  </Typography>
                </>
              )}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
};

export const RouteAdderRow: React.FC<{ onAdd: () => void }> = ({ onAdd }) => {
  return (
    <tr className="group">
      <td colSpan={5} className="px-6 py-4">
        <button
          onClick={onAdd}
          className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center gap-3 text-gray-600 hover:text-blue-600 group-hover:shadow-md"
        >
          <div className="w-8 h-8 bg-gray-100 group-hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors">
            <Plus size={16} />
          </div>
          <div className="text-left">
            <Typography variant="mediumParagraph" color="#374151" fontWeight={500}>
              Add New Route
            </Typography>
            <Typography variant="smallParagraph" color="#6B7280" style={{ marginTop: '0.125rem' }}>
              Configure routing for a phone number
            </Typography>
          </div>
        </button>
      </td>
    </tr>
  );
};
